/* GASSISA
========================================================================================
* Descripción: Modal para cierre de caja registradora
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 23/03/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 24/03/2026 | Lic. José Luis Aragón Cervantes  | Agregar impresión ticket cierre
*      3       | 04/06/2026 | Lic. José Luis Aragón Cervantes  | Homologar resumen y operaciones de cierre
*      4       | 19/10/2026 | Lic. José Luis Aragón Cervantes  | Ctrl+Alt+F8 ejecuta apertura manual de cajón de dinero
*      5       | 24/10/2026 | Lic. José Luis Aragón Cervantes  | Botón manual de cajón; retiro de atajo global
* ---------------------------------------------------------------------------------------
*/

import TextField from "../ui/TextField";
import React, { Fragment, useState, useEffect, useRef } from "react";
import { NumericFormat } from 'react-number-format';
import { Modal, Box, Typography, Button, Divider, Alert } from "@mui/material";
import { useSesion } from "../../context/System/SesionContext";
import { useLayout } from "../../context/System/LayoutContext";
import { useRequest } from "../../context/System/RequestContext";
import { apiCierreCaja } from "../../settings/ApiConfig";
import { getDateStringV3 } from "../../settings/utils";
import { buildCierreCajaTicket, buildTicketBytes } from "./cajaTicketUtils";
import { useVentas } from "../../context/Ventas/VentasContext";

const listaTiposMonedas = [
    { "TIPO": "MONEDA", "VALOR": 0.50 },
    { "TIPO": "MONEDA", "VALOR": 1 },
    { "TIPO": "MONEDA", "VALOR": 2 },
    { "TIPO": "MONEDA", "VALOR": 5 },
    { "TIPO": "MONEDA", "VALOR": 10 },
    { "TIPO": "BILLETE", "VALOR": 20 },
    { "TIPO": "BILLETE", "VALOR": 50 },
    { "TIPO": "BILLETE", "VALOR": 100 },
    { "TIPO": "BILLETE", "VALOR": 200 },
    { "TIPO": "BILLETE", "VALOR": 500 },
    { "TIPO": "BILLETE", "VALOR": 1000 }
];

const modalStyle = {
    position: 'fixed',
    top: '0',
    right: '25%',
    width: '45vw',
    maxWidth: '90vw',
    maxHeight: '100%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 3,
    borderRadius: 2,
    overflowY: 'auto', 
};

export default function ModalCierreCaja(props) {
    const { openModal = false, onClose, onSuccess, caja, statusCaja, resumenCorte = {}, usuario } = props;
    
    const { usuarioData, parametrosCajaForm } = useSesion();
    const { handleOpenAlert, handleOpenLoader, handleCloseLoader } = useLayout();
    const { PostRequest } = useRequest();
    const { handleObtenerResumenTotalesCaja, handleAbrirCajonDinero } = useVentas();

    const [internalOpen, setInternalOpen] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [listadoMonedas, setListadoMonedas] = useState(
        listaTiposMonedas.map(m => ({ ...m, Cantidad: 0 }))
    );
    const [observaciones, setObservaciones] = useState("");
    const [loading, setLoading] = useState(false);
    const [totalConteo, setTotalConteo] = useState(0);
    
    const primerInputRef = useRef(null);
    const cantidadInputsRef = useRef([]);
    const observacionesInputRef = useRef(null);
    const cerrarCajaButtonRef = useRef(null);

    const getResumenValue = (field) => Number(resumenCorte?.[field] ?? 0) || 0;
    const montoInicialCierre = getResumenValue("montoinicialapertura") || Number(statusCaja?.montoinicialapertura ?? 0) || 0;
    const totalEfectivoCierre = getResumenValue("totalefectivo");
    const totalDevolucionesCierre = getResumenValue("totaldevoluciones");
    const totalRetirosCierre = getResumenValue("totalretiros") || Number(statusCaja?.totalretiros ?? 0) || 0;
    const totalPrestamosCierre = getResumenValue("totalprestamos");
    const totalAbonosPrestamosCierre = getResumenValue("totalabonosprestamos");
    const totalValesPorCambioCierre = getResumenValue("totalvalesporcambio");
    const totalValesPorCambioGeneradosCierre = getResumenValue("totalvalesporcambiogenerados");
    const totalTarjetasCierre = getResumenValue("totaltarjetadebito") + getResumenValue("totaltarjetacredito") + getResumenValue("totalvales") + getResumenValue("totaltransferencia");
    const totalBancosCierre = getResumenValue("totalbancos") || totalTarjetasCierre;
    // Vales por cambio/diferencia: solo display en ticket, no afectan corte.
    // Abonos a préstamos = ingreso de capital a caja (backend ya lo incluye en totalefectivocierre)
    const totalEfectivoCierreCompleto = getResumenValue("totalefectivocierre") || (getResumenValue("totalabonosapartados") + getResumenValue("totalefectivocredito") + totalEfectivoCierre + totalAbonosPrestamosCierre);
    const absDeduccionesCierre = Math.abs(totalDevolucionesCierre) + Math.abs(totalRetirosCierre) + Math.abs(totalPrestamosCierre);
    const totalDeduccionesCierre = absDeduccionesCierre > 0 ? absDeduccionesCierre * -1 : getResumenValue("totaldeducciones");
    const totalGeneralCierre = totalBancosCierre + totalEfectivoCierreCompleto + totalDeduccionesCierre;
    // El efectivo esperado solo suma movimientos que afectan el cajón; los bancos no forman parte del conteo físico.
    const montoEsperadoCierre = montoInicialCierre + totalEfectivoCierreCompleto + totalDeduccionesCierre;
    const diferenciaCierre = montoEsperadoCierre - totalConteo;
    const resumenCorteRows = [
        { label: "Efectivo recibido", value: getResumenValue("totalefectivo") },
        { label: "Abono inicial crédito clientes", ticketLabel: "Abono inicial credito clientes", value: getResumenValue("totalefectivocredito") },
        { label: "Tarjeta débito", ticketLabel: "Tarjeta debito", value: getResumenValue("totaltarjetadebito") },
        { label: "Tarjeta crédito", ticketLabel: "Tarjeta credito", value: getResumenValue("totaltarjetacredito") },
        { label: "Transferencia", value: getResumenValue("totaltransferencia") },
        { label: "Vales", value: getResumenValue("totalvales") },
        { label: "Vales por cambio", value: Math.abs(totalValesPorCambioCierre) * -1 },
        { label: "Vales por cambio generados", value: getResumenValue("totalvalesporcambiogenerados") },
        { label: "Abonos apartados", value: getResumenValue("totalabonosapartados") },
        { label: "Abonos préstamos", ticketLabel: "Abonos prestamos", value: totalAbonosPrestamosCierre },
        { label: "Crédito clientes", ticketLabel: "Credito clientes", value: getResumenValue("totalcreditocliente") },
        { label: "Devoluciones", value: getResumenValue("totaldevoluciones") },
        { label: "Retiros", value: getResumenValue("totalretiros") },
        { label: "Préstamos", ticketLabel: "Prestamos", value: getResumenValue("totalprestamos") },
        { label: "Total general", value: totalGeneralCierre, strong: true },
    ];

    useEffect(() => {
        cantidadInputsRef.current = [];
    }, [listadoMonedas]);

    const handleKeyDown = (e, index, totalCampos) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();

            if (index < totalCampos - 1) {
                const nextInput = cantidadInputsRef.current[index + 1];
                if (nextInput) {
                    nextInput.focus();
                    nextInput.select();
                }
            } else if (index === totalCampos - 1) {
                if (observacionesInputRef.current) {
                    observacionesInputRef.current.focus();
                    observacionesInputRef.current.select();
                }
            }
        }
    };

    const handleObservacionesKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            if (cerrarCajaButtonRef.current) {
                cerrarCajaButtonRef.current.focus();
            }
        }
    };

    const handleOnFocus = (e) => {
        e.target.select();
    };

    useEffect(() => {
        if (openModal) {
            setInternalOpen(true);
            setListadoMonedas(listaTiposMonedas.map(m => ({ ...m, Cantidad: 0 })));
            setObservaciones("");
            setTotalConteo(0);
            setTimeout(() => setModalVisible(true), 50);
            setTimeout(() => {
                if (primerInputRef.current) {
                    primerInputRef.current.focus();
                    primerInputRef.current.select();
                }
            }, 100);
        } else {
            setModalVisible(false);
            setTimeout(() => setInternalOpen(false), 300);
        }
    }, [openModal]);

    useEffect(() => {
        const total = listadoMonedas.reduce((acc, item) => acc + (item.Cantidad * item.VALOR), 0);
        setTotalConteo(total);
    }, [listadoMonedas]);

    const handleUpdateListaMonedas = (tipo, valor, cantidad) => {
        const updatedList = listadoMonedas.map((item) =>
            item.TIPO === tipo && item.VALOR === valor
                ? { ...item, Cantidad: parseFloat(cantidad) || 0 }
                : item
        );
        setListadoMonedas(updatedList);
    };

    const handleCierreCaja = async () => {
        setLoading(true);
        handleOpenLoader("Procesando cierre de caja, por favor espere...");
        try {
            const listaMonedasFormateada = listadoMonedas.map(m => ({
                tipo: m.TIPO,
                valor: m.VALOR,
                cantidad: m.Cantidad
            }));

            const diferencia = diferenciaCierre;

            const data = {
                id: 0,
                caja: caja,
                folioapertura: 0,
                montocierre: totalConteo,
                montocajaactual: montoEsperadoCierre,
                diferencia: diferencia,
                totalventas: statusCaja?.totalventasdia || 0,
                totalabonosapartados: getResumenValue("totalabonosapartados"),
                totalcreditoclientes: getResumenValue("totalcreditocliente"),
                totaldevoluciones: totalDevolucionesCierre,
                totalretiros: totalRetirosCierre,
                totalcreditotarjetadebito: getResumenValue("totaltarjetadebito"),
                totalcreditotarjetacredito: getResumenValue("totaltarjetacredito"),
                totaleefectivo: totalEfectivoCierre,
                totaltransferencia: getResumenValue("totaltransferencia"),
                totalvales: getResumenValue("totalvales"),
                totalvalesporcambio: totalValesPorCambioCierre,
                totalvalesporcambiogenerados: totalValesPorCambioGeneradosCierre,
                totalprestamos: totalPrestamosCierre,
                totalabonosprestamos: totalAbonosPrestamosCierre,
                usuario: usuarioData?.Usuario || usuario,
                listacantidadmonedasbilletes: listaMonedasFormateada,
                observaciones: observaciones,
                foliocorte: 0
            };

            const response = await PostRequest({
                url: apiCierreCaja,
                body: data,
            });

            if (response.hasError) {
                handleOpenAlert(response.message, "error");
                return;
            }

            handleOpenAlert("Caja cerrada correctamente", "success");

            // Obtener resumen de totales actualizado para el ticket
            const resumen = await handleObtenerResumenTotalesCaja(
                getDateStringV3(new Date()),
                caja,
                usuarioData.Usuario || usuario
            );

            await imprimirTicketCierre(data, { ...(resumen || {}), ...resumenCorte });
            
            if (onSuccess) {
                onSuccess(response.result);
            }
            
            if (onClose) {
                onClose();
            }
        } catch (error) {
            handleOpenAlert("Error al cerrar caja: " + error.message, "error");
        } finally {
            setLoading(false);
            handleCloseLoader();
        }
    };

    const imprimirTicketCierre = async (dataCierre, resumenCorte) => {
        const textEncoder = new TextEncoder();

        const cab = resumenCorte || {};
        const cabValue = (field) => Number(cab?.[field] ?? 0) || 0;
        const montoInicial = cabValue("montoinicialapertura") || Number(statusCaja?.montoinicialapertura ?? 0) || 0;
        const totalEfectivo = cabValue("totalefectivo");
        const totalDevoluciones = cabValue("totaldevoluciones");
        const totalRetiros = cabValue("totalretiros") || Number(statusCaja?.totalretiros ?? 0) || 0;
        const totalPrestamos = cabValue("totalprestamos");
        const totalValesPorCambioGenerados = cabValue("totalvalesporcambiogenerados");
        const totalTarjetas = cabValue("totaltarjetadebito") + cabValue("totaltarjetacredito") + cabValue("totalvales") + cabValue("totaltransferencia");
        const totalBancos = cabValue("totalbancos") || totalTarjetas;
        const totalEfectivoCompleto = cabValue("totalefectivocierre") || (cabValue("totalabonosapartados") + cabValue("totalefectivocredito") + totalEfectivo + cabValue("totalabonosprestamos"));
        const absDeducciones = Math.abs(totalDevoluciones) + Math.abs(totalRetiros) + Math.abs(totalPrestamos);
        const totalDeducciones = absDeducciones > 0 ? absDeducciones * -1 : cabValue("totaldeducciones");
        const totalGeneral = totalBancos + totalEfectivoCompleto + totalDeducciones;
        const ventaTotal = totalBancos + totalEfectivoCompleto - Math.abs(totalDevoluciones);
        // Mantener el ticket físico con la misma conciliación de efectivo que el resumen del modal.
        const montoEsperado = montoInicial + totalEfectivoCompleto + totalDeducciones;
        const diferencia = montoEsperado - totalConteo;

        const ticketData = {
            ...dataCierre,
            ...cab,
            montoinicialapertura: montoInicial,
            totalbancos: totalBancos,
            totalefectivocierre: totalEfectivoCompleto,
            totaldeducciones: totalDeducciones,
            totalcapitalrestante: totalGeneral,
            ventatotal: ventaTotal,
            montocierre: totalConteo,
            diferencia,
            caja,
            usuario: usuarioData?.Usuario || usuario,
            observaciones,
        };

        const ticketParts = buildCierreCajaTicket({
            textEncoder,
            data: ticketData,
            formatCurrency,
            headerDate: new Date().toLocaleString('es-MX'),
            footerLabel: "CIERRE DE CAJA",
        });
        const finalTicket = buildTicketBytes(ticketParts);

        const formData = new FormData();
        formData.append("ticket", new Blob([finalTicket], { type: "application/octet-stream" }));
        formData.append("printerName", parametrosCajaForm?.nombreimpresoraventas?.trim() || "");

        const logoUrl = `${process.env.PUBLIC_URL}/recursos/logocliente.png`;
        try {
            const logoResponse = await fetch(logoUrl);
            if (logoResponse.ok) {
                const logoBlob = await logoResponse.blob();
                formData.append("logo", logoBlob, "logocliente.png");
            }
        } catch (err) {
            handleOpenAlert("Logo no disponible:", err);
        }

        try {
            const response2 = await fetch(`http://localhost:4000/api/imprimirticket`, {
                method: "POST",
                body: formData,
            });

            if (!response2.ok) {
                handleOpenAlert("Error en envío de impresión:", response2.statusText);
            }
        } catch (error) {
            handleOpenAlert("Error en impresión:", error.message);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(value || 0);
    };

    return (
        <Modal
            open={internalOpen}
            onClose={onClose}
            closeAfterTransition
        >
            <Box sx={{ ...modalStyle, transform: modalVisible ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 0.3s ease-in-out' }}>
                <Fragment>
                    <Typography variant="h6" component="h2" gutterBottom>
                        Cierre de Caja
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Caja: <strong>{caja}</strong> | Usuario: <strong>{usuarioData?.Usuario || usuario}</strong>
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                            Resumen del corte
                        </Typography>

                        {resumenCorteRows.map((row) => (
                            <Box
                                key={row.label}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    py: 0.35,
                                    borderTop: row.strong ? '1px solid' : 'none',
                                    borderColor: 'divider',
                                    mt: row.strong ? 0.5 : 0,
                                    pt: row.strong ? 0.85 : 0.35,
                                }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: row.strong ? 700 : 400 }}>
                                    {row.label}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: row.strong ? 700 : 400 }}>
                                    {formatCurrency(row.value)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                        Conteo de Efectivo
                    </Typography>

                    <Box sx={{ maxHeight: '100%', overflowY: 'auto', mb: 2 }}>
                        {listadoMonedas.map((moneda, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <Typography sx={{ minWidth: '80px' }}>
                                    {moneda.TIPO} ${moneda.VALOR}
                                </Typography>
                                <NumericFormat
                                    inputRef={(el) => {
                                        if (index === 0) primerInputRef.current = el;
                                        cantidadInputsRef.current[index] = el;
                                    }}
                                    value={moneda.Cantidad}
                                    thousandSeparator={true}
                                    decimalScale={0}
                                    fixedDecimalScale
                                    customInput={TextField}
                                    label="Cantidad"
                                    sx={{ width: '150px' }}
                                    size="small"
                                    onValueChange={(values) => handleUpdateListaMonedas(moneda.TIPO, moneda.VALOR, values.value)}
                                    onKeyDown={(e) => handleKeyDown(e, index, listadoMonedas.length)}
                                    onFocus={handleOnFocus}
                                />
                                <Typography sx={{ minWidth: '100px', textAlign: 'right' }}>
                                    = {formatCurrency(moneda.Cantidad * moneda.VALOR)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">
                            Total Conteo:
                        </Typography>
                        <Typography variant="h5" color="primary">
                            {formatCurrency(totalConteo)}
                        </Typography>
                    </Box>

                    {diferenciaCierre !== 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {diferenciaCierre > 0 ? "Faltante" : "Sobrante"}: {formatCurrency(Math.abs(diferenciaCierre))}
                        </Alert>
                    )}

                    <TextField autoComplete="off"
                        inputRef={observacionesInputRef}
                        label="Observaciones"
                        multiline
                        rows={2}
                        fullWidth
                        value={observaciones}
                        onChange={(e) => setObservaciones(e.target.value)}
                        onKeyDown={handleObservacionesKeyDown}
                        onFocus={handleOnFocus}
                        sx={{ mb: 2 }}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={() => handleAbrirCajonDinero()}
                            disabled={loading}
                        >
                            Abrir cajón
                        </Button>
                        <Button 
                            variant="outlined" 
                            onClick={onClose}
                            disabled={loading}
                        >
                            Cancelar
                        </Button>
                        <Button 
                            ref={cerrarCajaButtonRef}
                            variant="contained" 
                            color="error"
                            onClick={handleCierreCaja}
                            disabled={loading}
                        >
                            {loading ? "Cerrando..." : "Cerrar Caja"}
                        </Button>
                    </Box>
                </Fragment>
            </Box>
        </Modal>
    );
}
